import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ContactImportAction,
  ContactImportProcessDto,
  ExternalAddressImportDto,
  ExternalBankAccountImportDto,
  ExternalContactImportDto,
  ExternalEmailAddressImportDto,
  ExternalPhoneNumberImportDto,
  EMPTY_GUID,
  UpdateContactCompanyFromImportProcessDto,
} from '../../../../../models/ContactImportProcess';
import { makePrioStyles } from '../../../../../theme/utils';
import {
  Button,
  Drawer,
  Input,
  Select,
} from '@prio365/prio365-react-library';
import { Form, Table } from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import { apiUpdateContactsFromImportProcess } from '../../../api';

const useStyles = makePrioStyles((theme) => ({
  formItem: {
    marginBottom: theme.spacing.regular,
    width: '100%',
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing.regular,
  },
  sectionTitle: {
    fontWeight: 600,
    marginTop: theme.spacing.regular,
    marginBottom: theme.spacing.small,
  },
  inlineTable: {
    marginBottom: theme.spacing.regular,
  },
  existingLink: {
    color: '#1890ff',
    marginBottom: theme.spacing.regular,
  },
}));

interface ExternalContactDrawerProps {
  visible: boolean;
  onClose: () => void;
  contact: ExternalContactImportDto;
  contactImportProcess: ContactImportProcessDto | null;
  onProcessUpdate: (updatedProcess: ContactImportProcessDto) => void;
}

const contactImportActionOptions = (t: any) => [
  {
    value: ContactImportAction.NotSet,
    label: t('settings:companyImport.drawerActions.notSet'),
  },
  {
    value: ContactImportAction.Skip,
    label: t('settings:companyImport.drawerActions.skip'),
  },
  {
    value: ContactImportAction.UpdateMissing,
    label: t('settings:companyImport.drawerActions.updateMissing'),
  },
  {
    value: ContactImportAction.OverrideExisting,
    label: t('settings:companyImport.drawerActions.overrideExisting'),
  },
  {
    value: ContactImportAction.Create,
    label: t('settings:companyImport.drawerActions.create'),
  },
];

const ExternalContactDrawer: React.FC<ExternalContactDrawerProps> = ({
  visible,
  onClose,
  contact,
  contactImportProcess,
  onProcessUpdate,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const [emailAddresses, setEmailAddresses] = useState<
    ExternalEmailAddressImportDto[]
  >([]);
  const [phoneNumbers, setPhoneNumbers] = useState<
    ExternalPhoneNumberImportDto[]
  >([]);
  const [addresses, setAddresses] = useState<ExternalAddressImportDto[]>([]);
  const [bankAccounts, setBankAccounts] = useState<
    ExternalBankAccountImportDto[]
  >([]);

  useEffect(() => {
    if (contact) {
      form.setFieldsValue({
        salutation: contact.salutation,
        firstName: contact.firstName,
        lastName: contact.lastName,
        title: contact.title,
        department: contact.department,
        position: contact.position,
        website: contact.website,
        notes: contact.notes,
        action: contact.action,
      });
      setEmailAddresses(contact.emailAddresses || []);
      setPhoneNumbers(contact.phoneNumbers || []);
      setAddresses(contact.addresses || []);
      setBankAccounts(contact.bankAccounts || []);
    }
  }, [contact, form]);

  const handleFinish = async (values: any) => {
    if (!contact || !contactImportProcess) return;

    const updateDto: UpdateContactCompanyFromImportProcessDto = {
      contactImportProcessId:
        contactImportProcess.contactImportProcessId,
      updateExternalContactImports: [
        {
          externalContactImportId: contact.externalContactImportId,
          externalCompanyImportId: contact.externalCompanyImportId,
          externalBusinessUnitImportId: contact.externalBusinessUnitImportId,
          salutation: values.salutation || '',
          firstName: values.firstName || '',
          lastName: values.lastName || '',
          title: values.title || '',
          department: values.department || '',
          position: values.position || '',
          website: values.website || '',
          notes: values.notes || '',
          emailAddresses: emailAddresses.map((e) => ({
            externalEmailAddressImportId: e.externalEmailAddressImportId,
            address: e.address,
            notes: e.notes || '',
            isPrimary: e.isPrimary,
          })),
          phoneNumbers: phoneNumbers.map((p) => ({
            externalPhoneNumberImportId: p.externalPhoneNumberImportId,
            number: p.number,
            notes: p.notes || '',
            phoneType: p.phoneType,
            isPrimary: p.isPrimary,
          })),
          addresses: addresses.map((a) => ({
            externalAddressImportId: a.externalAddressImportId,
            name: a.name || '',
            street: a.street || '',
            zip: a.zip || '',
            city: a.city || '',
            country: a.country || '',
            region: a.region || '',
            secondAddressLine: a.secondAddressLine || '',
            notes: a.notes || '',
            isPrimary: a.isPrimary,
          })),
          bankAccounts: bankAccounts.map((b) => ({
            externalBankAccountImportId: b.externalBankAccountImportId,
            creditInstitutionName: b.creditInstitutionName || '',
            bic: b.bic || '',
            iban: b.iban || '',
            notes: b.notes || '',
            isPrimary: b.isPrimary,
          })),
          action: values.action,
          existingContactId: contact.existingContactId,
          existingCompanyId: contact.existingCompanyId,
          existingBusinessUnitId: contact.existingBusinessUnitId,
          description: contact.description || '',
        },
      ],
    };

    try {
      const result = await apiUpdateContactsFromImportProcess(
        contactImportProcess.contactImportProcessId,
        updateDto
      );

      if (result.result.ok) {
        const updatedProcess = {
          ...contactImportProcess,
          externalContactImports:
            (contactImportProcess.externalContactImports ?? []).map((c) =>
              c.externalContactImportId === contact.externalContactImportId
                ? {
                    ...c,
                    ...values,
                    emailAddresses,
                    phoneNumbers,
                    addresses,
                    bankAccounts,
                  }
                : c
            ),
        };
        onProcessUpdate(updatedProcess);
        onClose();
      }
    } catch (error) {
      console.error('Update failed:', error);
    }
  };

  const emailColumns = [
    {
      title: t('settings:companyImport.table.email'),
      dataIndex: 'address',
      key: 'address',
    },
    {
      title: t('settings:companyImport.table.isPrimary'),
      dataIndex: 'isPrimary',
      key: 'isPrimary',
      render: (v: boolean) => (v ? 'Ja' : 'Nein'),
    },
    {
      title: t('settings:companyImport.table.notes'),
      dataIndex: 'notes',
      key: 'notes',
    },
  ];

  const phoneColumns = [
    {
      title: t('settings:companyImport.table.number'),
      dataIndex: 'number',
      key: 'number',
    },
    {
      title: t('settings:companyImport.table.phoneType'),
      dataIndex: 'phoneType',
      key: 'phoneType',
      render: (v: number) => t(`settings:companyImport.phoneType.${v}`),
    },
    {
      title: t('settings:companyImport.table.isPrimary'),
      dataIndex: 'isPrimary',
      key: 'isPrimary',
      render: (v: boolean) => (v ? 'Ja' : 'Nein'),
    },
  ];

  const addressColumns = [
    {
      title: t('settings:companyImport.table.street'),
      dataIndex: 'street',
      key: 'street',
    },
    {
      title: t('settings:companyImport.table.zip'),
      dataIndex: 'zip',
      key: 'zip',
    },
    {
      title: t('settings:companyImport.table.city'),
      dataIndex: 'city',
      key: 'city',
    },
    {
      title: t('settings:companyImport.table.country'),
      dataIndex: 'country',
      key: 'country',
    },
    {
      title: t('settings:companyImport.table.region'),
      dataIndex: 'region',
      key: 'region',
    },
  ];

  const bankColumns = [
    {
      title: t('settings:companyImport.table.iban'),
      dataIndex: 'iban',
      key: 'iban',
    },
    {
      title: t('settings:companyImport.table.bic'),
      dataIndex: 'bic',
      key: 'bic',
    },
    {
      title: t('settings:companyImport.table.creditInstitutionName'),
      dataIndex: 'creditInstitutionName',
      key: 'creditInstitutionName',
    },
    {
      title: t('settings:companyImport.table.isPrimary'),
      dataIndex: 'isPrimary',
      key: 'isPrimary',
      render: (v: boolean) => (v ? 'Ja' : 'Nein'),
    },
  ];

  const hasExistingLink = (id: string) => id && id !== EMPTY_GUID;

  return (
    <Drawer
      title={`${contact?.firstName} ${contact?.lastName}`}
      onClose={onClose}
      visible={visible}
      width={'default'}
    >
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        {hasExistingLink(contact?.existingContactId) && (
          <div className={classes.existingLink}>
            {t('settings:companyImport.existingContact')}: {contact.existingContactId}
          </div>
        )}

        <Form.Item
          name="salutation"
          label={t('settings:companyImport.table.salutation')}
          className={classes.formItem}
        >
          <Input full />
        </Form.Item>

        <Form.Item
          name="firstName"
          label={t('settings:companyImport.table.firstName')}
          className={classes.formItem}
        >
          <Input full />
        </Form.Item>

        <Form.Item
          name="lastName"
          label={t('settings:companyImport.table.lastName')}
          className={classes.formItem}
        >
          <Input full />
        </Form.Item>

        <Form.Item
          name="title"
          label={t('settings:companyImport.table.title')}
          className={classes.formItem}
        >
          <Input full />
        </Form.Item>

        <Form.Item
          name="department"
          label={t('settings:companyImport.table.department')}
          className={classes.formItem}
        >
          <Input full />
        </Form.Item>

        <Form.Item
          name="position"
          label={t('settings:companyImport.table.position')}
          className={classes.formItem}
        >
          <Input full />
        </Form.Item>

        <Form.Item
          name="website"
          label={t('settings:companyImport.table.website')}
          className={classes.formItem}
        >
          <Input full />
        </Form.Item>

        <Form.Item
          name="notes"
          label={t('settings:companyImport.table.notes')}
          className={classes.formItem}
        >
          <TextArea rows={2} />
        </Form.Item>

        <Form.Item
          name="action"
          label={t('settings:companyImport.table.action')}
          className={classes.formItem}
        >
          <Select options={contactImportActionOptions(t)} />
        </Form.Item>

        {emailAddresses.length > 0 && (
          <>
            <div className={classes.sectionTitle}>
              {t('settings:companyImport.table.emailAddresses')}
            </div>
            <Table
              dataSource={emailAddresses}
              columns={emailColumns}
              rowKey="externalEmailAddressImportId"
              pagination={false}
              size="small"
              className={classes.inlineTable}
            />
          </>
        )}

        {phoneNumbers.length > 0 && (
          <>
            <div className={classes.sectionTitle}>
              {t('settings:companyImport.table.phoneNumbers')}
            </div>
            <Table
              dataSource={phoneNumbers}
              columns={phoneColumns}
              rowKey="externalPhoneNumberImportId"
              pagination={false}
              size="small"
              className={classes.inlineTable}
            />
          </>
        )}

        {addresses.length > 0 && (
          <>
            <div className={classes.sectionTitle}>
              {t('settings:companyImport.table.addresses')}
            </div>
            <Table
              dataSource={addresses}
              columns={addressColumns}
              rowKey="externalAddressImportId"
              pagination={false}
              size="small"
              className={classes.inlineTable}
            />
          </>
        )}

        {bankAccounts.length > 0 && (
          <>
            <div className={classes.sectionTitle}>
              {t('settings:companyImport.table.bankAccounts')}
            </div>
            <Table
              dataSource={bankAccounts}
              columns={bankColumns}
              rowKey="externalBankAccountImportId"
              pagination={false}
              size="small"
              className={classes.inlineTable}
            />
          </>
        )}

        <div className={classes.buttonContainer}>
          <Button type="primary" htmlType="submit">
            {t('common:actions.save')}
          </Button>
          <Button style={{ marginLeft: 8 }} onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
        </div>
      </Form>
    </Drawer>
  );
};

export default ExternalContactDrawer;
